/**
 * JUICE · shared motion layer.
 *
 * Asana T2: "Text rising, pulsing when hover over, colour change etc. Subtle changes
 * for more engaging interactions." and T5: "Can this become an animation?" for the
 * homepage stat figures.
 *
 * Two behaviours, both opt-in via data attribute so nothing animates by accident:
 *   [data-jx-rise]        — fades and rises into place the first time it's scrolled to
 *   [data-jx-count]       — counts a number up when it first becomes visible
 *
 * Everything is skipped entirely under prefers-reduced-motion: the elements are just
 * shown in their final state.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const rise = Array.from(document.querySelectorAll('[data-jx-rise]'));
  const counters = Array.from(document.querySelectorAll('[data-jx-count]'));
  if (!rise.length && !counters.length) return;

  if (reduced || !('IntersectionObserver' in window)) {
    rise.forEach((el) => el.classList.add('jx-rise--in'));
    return;
  }

  // ---- rise-in --------------------------------------------------------------
  if (rise.length) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          // Stagger siblings so a row of cards arrives as a sequence, not a block.
          const delay = Number(entry.target.dataset.jxRise) || 0;
          entry.target.style.transitionDelay = `${delay}ms`;
          entry.target.classList.add('jx-rise--in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );
    rise.forEach((el) => io.observe(el));
  }

  // ---- count-up -------------------------------------------------------------
  // Figures carry their own formatting ("100+", "1.22M", "4.6", "26"). Rather than
  // parse a format string, split the label into a numeric core plus whatever
  // prefix/suffix surrounds it, animate the core, and reassemble.
  const parse = (text) => {
    const m = String(text).trim().match(/^([^\d.-]*)(-?[\d,]*\.?\d+)(.*)$/s);
    if (!m) return null;
    const raw = m[2].replace(/,/g, '');
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return null;
    const dot = raw.indexOf('.');
    return {
      prefix: m[1],
      suffix: m[3],
      value,
      decimals: dot === -1 ? 0 : raw.length - dot - 1,
      grouped: m[2].includes(','),
    };
  };

  const format = (n, spec) => {
    const fixed = n.toFixed(spec.decimals);
    const withGroups = spec.grouped
      ? Number(fixed).toLocaleString(undefined, {
          minimumFractionDigits: spec.decimals,
          maximumFractionDigits: spec.decimals,
        })
      : fixed;
    return spec.prefix + withGroups + spec.suffix;
  };

  if (counters.length) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          io.unobserve(el);

          const spec = parse(el.textContent);
          if (!spec) return;

          const duration = Number(el.dataset.jxCount) || 1100;
          const start = performance.now();
          // easeOutCubic — quick out of the gate, settles rather than stops dead.
          const ease = (t) => 1 - Math.pow(1 - t, 3);

          const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            el.textContent = format(spec.value * ease(t), spec);
            if (t < 1) requestAnimationFrame(step);
            else el.textContent = format(spec.value, spec);
          };
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach((el) => io.observe(el));
  }
})();
